// CRM-справочники: источники, стадии, причины, скидки, оплата (выделено из reference.api.ts планом 108, Ф4).
import { api } from '@/lib/api';

import { defined, type ListParams } from './_shared.js';

// --- CRM: источники сделок + этапы воронки — crm-модуль ----------------------

export type StageKind = 'open' | 'client' | 'won' | 'lost' | 'invalid';

export interface DealSource {
  id: string;
  name: string;
  sortOrder: number;
  isArchived: boolean;
}
export interface DealStage {
  id: string;
  name: string;
  kind: StageKind;
  sortOrder: number;
  isArchived: boolean;
  /** Цвет колонки канбана сделок (#rrggbb), docs/service/52. null — дефолт UI. */
  color: string | null;
}

export interface CreateSourceBody {
  name: string;
  sortOrder?: number;
}
export interface UpdateSourceBody {
  name?: string;
  sortOrder?: number;
  isArchived?: boolean;
}
export interface CreateStageBody {
  name: string;
  kind?: StageKind;
  sortOrder?: number;
  color?: string | null;
}
export interface UpdateStageBody {
  name?: string;
  kind?: StageKind;
  sortOrder?: number;
  isArchived?: boolean;
  color?: string | null;
}

export function listDealSources(params: ListParams = {}): Promise<{ items: DealSource[] }> {
  const sp = new URLSearchParams();
  if (params.includeArchived) sp.set('include_archived', 'true');
  const qs = sp.toString();
  return api.get<{ items: DealSource[] }>(`/crm/sources${qs ? `?${qs}` : ''}`);
}
export function createDealSource(body: CreateSourceBody): Promise<{ source: DealSource }> {
  return api.post('/crm/sources', defined({ name: body.name, sort_order: body.sortOrder }));
}
export function updateDealSource(
  id: string,
  body: UpdateSourceBody,
): Promise<{ source: DealSource }> {
  return api.patch(
    `/crm/sources/${id}`,
    defined({ name: body.name, sort_order: body.sortOrder, is_archived: body.isArchived }),
  );
}
export function archiveDealSource(id: string): Promise<void> {
  return api.delete<void>(`/crm/sources/${id}`);
}

// --- Причины исхода сделки (план 58) -----------------------------------------

/** Причина провала либо некорректности лида. */
export interface StageReason {
  id: string;
  name: string;
  kind: 'lost' | 'invalid';
  sortOrder: number;
  isArchived: boolean;
}
export interface CreateReasonBody {
  kind: 'lost' | 'invalid';
  name: string;
  sortOrder?: number;
}
export interface UpdateReasonBody {
  name?: string;
  sortOrder?: number;
  isArchived?: boolean;
}

export function listStageReasons(params: ListParams = {}): Promise<{ items: StageReason[] }> {
  const sp = new URLSearchParams();
  if (params.includeArchived) sp.set('include_archived', 'true');
  const qs = sp.toString();
  return api.get<{ items: StageReason[] }>(`/crm/stage-reasons${qs ? `?${qs}` : ''}`);
}
export function createStageReason(body: CreateReasonBody): Promise<{ reason: StageReason }> {
  return api.post(
    '/crm/stage-reasons',
    defined({ kind: body.kind, name: body.name, sort_order: body.sortOrder }),
  );
}
export function updateStageReason(
  id: string,
  body: UpdateReasonBody,
): Promise<{ reason: StageReason }> {
  return api.patch(
    `/crm/stage-reasons/${id}`,
    defined({ name: body.name, sort_order: body.sortOrder, is_archived: body.isArchived }),
  );
}
export function archiveStageReason(id: string): Promise<void> {
  return api.delete<void>(`/crm/stage-reasons/${id}`);
}

export function listDealStages(params: ListParams = {}): Promise<{ items: DealStage[] }> {
  const sp = new URLSearchParams();
  if (params.includeArchived) sp.set('include_archived', 'true');
  const qs = sp.toString();
  return api.get<{ items: DealStage[] }>(`/crm/stages${qs ? `?${qs}` : ''}`);
}
export function createDealStage(body: CreateStageBody): Promise<{ stage: DealStage }> {
  return api.post(
    '/crm/stages',
    defined({ name: body.name, kind: body.kind, sort_order: body.sortOrder, color: body.color }),
  );
}
export function updateDealStage(id: string, body: UpdateStageBody): Promise<{ stage: DealStage }> {
  return api.patch(
    `/crm/stages/${id}`,
    // kind и sort_order сервер больше НЕ принимает (план 77): вид задаётся при
    // создании, порядок — целым документом. Шли мы их — получили бы 422.
    defined({
      name: body.name,
      is_archived: body.isArchived,
      color: body.color,
    }),
  );
}
export function archiveDealStage(id: string): Promise<void> {
  return api.delete<void>(`/crm/stages/${id}`);
}

/** Порядок стадий целиком (план 77): частичных перестановок нет. */
export function reorderDealStages(ids: string[]): Promise<void> {
  return api.put<void>('/crm/stages/order', { ids });
}

/**
 * Жёсткое удаление стадии с переносом сделок (план 77).
 *
 * POST, а не DELETE: глагол DELETE у этого ресурса — АРХИВ (обратимый), и путать
 * их нельзя. Без `moveTo` сервер отвечает 409 с числом сделок — интерфейс по
 * этому ответу спрашивает, куда их переложить.
 */
export function deleteDealStage(id: string, moveTo?: string): Promise<void> {
  return api.post<void>(`/crm/stages/${id}/delete`, moveTo ? { move_to: moveTo } : {});
}

// --- Скидки (справочник) ----------------------------------------------------
export type DiscountType = 'percent' | 'fixed';

export interface Discount {
  id: string;
  name: string;
  type: DiscountType;
  /** percent → %, fixed → рубли. */
  value: number;
  sortOrder: number;
  isArchived: boolean;
  /** Филиалы скидки (план 128 Ф3). Пусто — действует во всех филиалах. */
  branchIds: string[];
}
export interface CreateDiscountBody {
  name: string;
  type: DiscountType;
  value: number;
  sortOrder?: number;
  branchIds?: string[];
}
export interface UpdateDiscountBody {
  name?: string;
  type?: DiscountType;
  value?: number;
  sortOrder?: number;
  isArchived?: boolean;
  branchIds?: string[];
}

export function listDiscounts(
  params: ListParams & { branchId?: string } = {},
): Promise<{ items: Discount[] }> {
  const sp = new URLSearchParams();
  if (params.includeArchived) sp.set('include_archived', 'true');
  if (params.branchId) sp.set('branch_id', params.branchId);
  const qs = sp.toString();
  return api.get<{ items: Discount[] }>(`/discounts${qs ? `?${qs}` : ''}`);
}
export function createDiscount(body: CreateDiscountBody): Promise<{ discount: Discount }> {
  return api.post(
    '/discounts',
    defined({
      name: body.name,
      type: body.type,
      value: body.value,
      sort_order: body.sortOrder,
      branch_ids: body.branchIds,
    }),
  );
}
export function updateDiscount(
  id: string,
  body: UpdateDiscountBody,
): Promise<{ discount: Discount }> {
  return api.patch(
    `/discounts/${id}`,
    defined({
      name: body.name,
      type: body.type,
      value: body.value,
      sort_order: body.sortOrder,
      is_archived: body.isArchived,
      branch_ids: body.branchIds,
    }),
  );
}
export function archiveDiscount(id: string): Promise<void> {
  return api.delete<void>(`/discounts/${id}`);
}

// --- Способы оплаты (справочник) --------------------------------------------
// code ∈ enum PaymentMethod, неизменяем; редактируются label/порядок/архивность.
export type PaymentMethodCode =
  | 'cash'
  | 'bank_transfer'
  | 'card_transfer'
  | 'terminal'
  | 'qr'
  | 'certificate'
  | 'deposit'
  | 'debt'
  | 'legacy';

export interface PaymentMethodItem {
  id: string;
  code: PaymentMethodCode;
  label: string;
  sortOrder: number;
  isArchived: boolean;
}
export interface UpdatePaymentMethodBody {
  label?: string;
  sortOrder?: number;
  isArchived?: boolean;
}

export function listPaymentMethods(
  params: ListParams = {},
): Promise<{ items: PaymentMethodItem[] }> {
  const sp = new URLSearchParams();
  if (params.includeArchived) sp.set('include_archived', 'true');
  const qs = sp.toString();
  return api.get<{ items: PaymentMethodItem[] }>(`/payment-methods${qs ? `?${qs}` : ''}`);
}
export function updatePaymentMethod(
  id: string,
  body: UpdatePaymentMethodBody,
): Promise<{ paymentMethod: PaymentMethodItem }> {
  return api.patch(
    `/payment-methods/${id}`,
    defined({ label: body.label, sort_order: body.sortOrder, is_archived: body.isArchived }),
  );
}
